import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, RefreshCw, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PWAUpdateNotificationProps {
  onUpdate: () => void;
  onDismiss: () => void;
}

export const PWAUpdateNotification: React.FC<PWAUpdateNotificationProps> = ({
  onUpdate,
  onDismiss
}) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await onUpdate();
    } catch (error) {
      console.error('Update failed:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4">
      <Card className="w-80 shadow-lg border-gebeya-pink/20 bg-white backdrop-blur-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold text-gebeya-purple">
              {t('pwa.updateNotification.title')}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <CardDescription className="text-xs text-gray-600 mb-3">
            {t('pwa.updateNotification.description')}
          </CardDescription>
          <div className="flex gap-2">
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="flex-1 bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white text-xs font-medium transition-opacity"
              size="sm"
            >
              {isUpdating ? (
                <>
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  {t('pwa.updateNotification.updatingButton')}
                </>
              ) : (
                <>
                  <Download className="h-3 w-3 mr-1" />
                  {t('pwa.updateNotification.updateButton')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onDismiss}
              className="text-xs border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 font-medium transition-colors"
              size="sm"
            >
              {t('pwa.updateNotification.laterButton')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to manage PWA updates
export const usePWAUpdate = () => {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [updateSW, setUpdateSW] = useState<(() => Promise<void>) | null>(null);

  useEffect(() => {
    // Listen for the pwa:setUpdateSW event from main.tsx
    const handleSetUpdateSW = (e: CustomEvent) => {
      if (e.detail && typeof e.detail === 'function') {
        setUpdateSW(() => e.detail);
        setNeedRefresh(true); // Set needRefresh to true when update is available
      }
    };

    window.addEventListener('pwa:setUpdateSW', handleSetUpdateSW as EventListener);
    
    // Listen for service worker controller changes
    if ('serviceWorker' in navigator) {
      const handleControllerChange = () => {
        console.log('Service worker controller changed, reloading page...');
        window.location.reload();
      };
      
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      
      return () => {
        window.removeEventListener('pwa:setUpdateSW', handleSetUpdateSW as EventListener);
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };
    }

    return () => {
      window.removeEventListener('pwa:setUpdateSW', handleSetUpdateSW as EventListener);
    };
  }, []);

  const handleUpdate = async () => {
    if (updateSW) {
      try {
        await updateSW();
        // The updateSW function should handle the service worker update
        // The controllerchange event listener will handle the page reload
      } catch (error) {
        console.error('Update failed:', error);
      }
    }
  };

  const dismissUpdate = () => {
    setNeedRefresh(false);
  };

  return {
    needRefresh,
    offlineReady,
    handleUpdate,
    dismissUpdate,
    setUpdateSW
  };
}; 